import React, { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { Star, Plus, Pencil, Trash2, PackageX } from "lucide-react";
import {
  Field, TextInput, TextArea, SingleImageUploader, GalleryUploader, Modal,
  CATEGORY_OPTIONS, slugPreview,
} from "@/pages/admin/adminShared";

const EMPTY_FORM = {
  id: null, name: "", slug: "", category: CATEGORY_OPTIONS[0].value, sku: "",
  short_desc: "", description: "", price: "", unit: "", weight_g: "",
  stock: 0, featured: false, active: true, image: "", images: [],
};

export default function AdminProducts() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [form, setForm] = useState(null); // null = modal cerrado
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  const load = useCallback(() => {
    setLoading(true);
    api.get("/admin/products").then((r) => {
      setProducts(r.data);
      setLoading(false);
    });
  }, []);

  useEffect(() => { load(); }, [load]);

  const update = async (id, patch) => {
    try {
      const { data } = await api.patch(`/admin/products/${id}`, patch);
      setProducts((ps) => ps.map((p) => (p.id === id ? (data || { ...p, ...patch }) : p)));
    } catch (e) {
      alert(e.response?.data?.detail || "Error al actualizar");
    }
  };

  const updateVariantPrice = async (product, variantId, newPrice) => {
    const variants = (product.variants || []).map((v) =>
      v.id === variantId ? { ...v, price: parseFloat(newPrice) } : v
    );
    const primary = variants[0];
    await update(product.id, { variants, price: primary?.price ?? product.price });
  };

  const openCreate = () => { setErr(""); setForm({ ...EMPTY_FORM }); };
  const openEdit = (p) => {
    setErr("");
    setForm({
      id: p.id, name: p.name || "", slug: p.slug || "", category: p.category || CATEGORY_OPTIONS[0].value,
      sku: p.sku || "", short_desc: p.short_desc || "", description: p.description || "",
      price: p.price ?? "", unit: p.unit || "", weight_g: p.weight_g || "",
      stock: p.stock ?? 0, featured: !!p.featured, active: p.active !== false,
      image: p.image || "", images: p.images || [],
    });
  };
  const closeForm = () => setForm(null);

  const save = async (e) => {
    e.preventDefault();
    setErr("");
    if (!form.name.trim()) { setErr("El nombre es obligatorio"); return; }
    if (!form.price || parseFloat(form.price) <= 0) { setErr("El precio debe ser mayor a 0"); return; }
    setSaving(true);
    const payload = {
      name: form.name.trim(),
      slug: form.slug.trim() || undefined,
      category: form.category,
      sku: form.sku.trim() || undefined,
      short_desc: form.short_desc.trim() || undefined,
      description: form.description.trim(),
      price: parseFloat(form.price),
      unit: form.unit.trim() || undefined,
      weight_g: form.weight_g ? parseFloat(form.weight_g) : undefined,
      stock: parseInt(form.stock, 10) || 0,
      featured: form.featured,
      active: form.active,
      image: form.image || undefined,
      images: form.images,
    };
    try {
      if (form.id) {
        await api.patch(`/admin/products/${form.id}`, payload);
      } else {
        await api.post("/admin/products", payload);
      }
      closeForm();
      load();
    } catch (e2) {
      setErr(e2.response?.data?.detail || "Error al guardar el producto");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`¿Eliminar "${p.name}"? Esta acción no se puede deshacer.`)) return;
    try {
      await api.delete(`/admin/products/${p.id}`);
      load();
    } catch (e) {
      alert(e.response?.data?.detail || "Error al eliminar");
    }
  };

  const filtered = products.filter((p) => !filter || p.category === filter);
  const categories = [...new Set(products.map((p) => p.category))];

  return (
    <div className="p-8 md:p-12" data-testid="admin-products">
      <div className="flex items-start justify-between mb-8 flex-wrap gap-4">
        <div>
          <div className="text-xs tracking-[0.35em] uppercase text-arre-red mb-2">⟡ Admin</div>
          <h1 className="font-display text-4xl md:text-5xl tracking-wider uppercase text-white leading-none">Productos</h1>
        </div>
        <button
          onClick={openCreate}
          className="btn-arre bg-arre-red hover:bg-arre-red-dark text-white font-display text-sm tracking-widest uppercase px-5 py-3 inline-flex items-center gap-2"
          data-testid="new-product-btn"
        >
          <Plus className="w-4 h-4" /> Nuevo producto
        </button>
      </div>

      <div className="flex gap-2 mb-4 flex-wrap">
        <button
          onClick={() => setFilter("")}
          className={`px-4 py-2 font-display text-sm tracking-widest uppercase border transition ${
            !filter ? "bg-arre-red border-arre-red text-white" : "border-arre-border text-white/60 hover:text-white"
          }`}
        >
          Todos ({products.length})
        </button>
        {categories.map((c) => (
          <button
            key={c}
            onClick={() => setFilter(c)}
            className={`px-4 py-2 font-display text-sm tracking-widest uppercase border transition ${
              filter === c ? "bg-arre-red border-arre-red text-white" : "border-arre-border text-white/60 hover:text-white"
            }`}
          >
            {c}
          </button>
        ))}
      </div>

      <div className="bg-arre-surface border border-arre-border overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-arre-border text-[10px] tracking-[0.2em] uppercase text-white/50">
              <th className="text-left p-4">Producto</th>
              <th className="text-left p-4">Categoría</th>
              <th className="text-left p-4">Precios (editables)</th>
              <th className="text-left p-4">Stock</th>
              <th className="text-left p-4">Destacado</th>
              <th className="text-left p-4">Activo</th>
              <th className="text-left p-4"></th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan="7" className="p-8 text-center text-white/50">Cargando...</td></tr>}
            {!loading && filtered.length === 0 && (
              <tr><td colSpan="7" className="p-10 text-center">
                <PackageX className="w-10 h-10 text-white/20 mx-auto mb-3" />
                <div className="text-white/50">Sin productos todavía. Crea el primero, compare.</div>
              </td></tr>
            )}
            {filtered.map((p) => (
              <tr key={p.id} className="border-b border-arre-border/50" data-testid={`admin-product-${p.slug}`}>
                <td className="p-4">
                  <div className="flex items-center gap-3">
                    <img src={p.image} alt={p.name} className="w-10 h-10 object-cover border border-arre-border" />
                    <div>
                      <div className="font-display text-white">{p.name}</div>
                      <div className="text-xs text-white/40">{p.sku}</div>
                    </div>
                  </div>
                </td>
                <td className="p-4 text-white/70 capitalize">{p.category}</td>
                <td className="p-4">
                  <div className="space-y-1.5">
                    {(p.variants || [{ id: "default", label: p.unit || "Precio", price: p.price }]).map((v) => (
                      <div key={v.id} className="flex items-center gap-2">
                        <span className="text-[10px] tracking-widest uppercase text-white/50 w-16 text-right">
                          {v.id === "350g" ? "350 g" : v.id === "1kg" ? "1 kg" : v.label?.split(" ")[0] || v.id}
                        </span>
                        <span className="text-white/60 text-sm">$</span>
                        <input
                          type="number"
                          min="0"
                          step="1"
                          defaultValue={v.price}
                          onBlur={(e) => {
                            const nv = parseFloat(e.target.value);
                            if (!isNaN(nv) && nv > 0 && nv !== v.price) {
                              if (p.variants?.length) {
                                updateVariantPrice(p, v.id, nv);
                              } else {
                                update(p.id, { price: nv });
                              }
                            }
                          }}
                          className="w-24 bg-arre-black border border-arre-border px-2 py-1 text-white text-sm focus:outline-none focus:border-arre-red"
                          data-testid={`price-${p.slug}-${v.id}`}
                        />
                      </div>
                    ))}
                  </div>
                </td>
                <td className="p-4">
                  <input
                    type="number"
                    min="0"
                    defaultValue={p.stock ?? 0}
                    onBlur={(e) => {
                      const v = parseInt(e.target.value, 10);
                      if (!isNaN(v) && v !== p.stock) update(p.id, { stock: v });
                    }}
                    className="w-20 bg-arre-black border border-arre-border px-2 py-1 text-white focus:outline-none focus:border-arre-red"
                    data-testid={`stock-${p.slug}`}
                  />
                </td>
                <td className="p-4">
                  <button
                    onClick={() => update(p.id, { featured: !p.featured })}
                    className={`${p.featured ? "text-arre-red" : "text-white/30"} hover:scale-110 transition`}
                    data-testid={`featured-${p.slug}`}
                    title={p.featured ? "Destacado" : "No destacado"}
                  >
                    <Star className="w-5 h-5" fill={p.featured ? "currentColor" : "none"} />
                  </button>
                </td>
                <td className="p-4">
                  <Toggle active={p.active !== false} onChange={(v) => update(p.id, { active: v })} testid={`active-${p.slug}`} />
                </td>
                <td className="p-4">
                  <div className="flex items-center gap-3">
                    <button onClick={() => openEdit(p)} className="text-white/50 hover:text-white" title="Editar" data-testid={`edit-product-${p.slug}`}>
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button onClick={() => remove(p)} className="text-white/40 hover:text-arre-red" title="Eliminar" data-testid={`delete-product-${p.slug}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {form && (
        <Modal title={form.id ? "Editar producto" : "Nuevo producto"} onClose={closeForm} wide>
          <form onSubmit={save} className="space-y-4" data-testid="product-form">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Nombre">
                <TextInput
                  required
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value, slug: form.id ? form.slug : slugPreview(e.target.value) })}
                  placeholder="Ribeye"
                  data-testid="product-name-input"
                />
              </Field>
              <Field label="Slug (URL)">
                <TextInput
                  value={form.slug}
                  onChange={(e) => setForm({ ...form, slug: e.target.value })}
                  placeholder="ribeye"
                />
              </Field>
              <Field label="Categoría">
                <select
                  value={form.category}
                  onChange={(e) => setForm({ ...form, category: e.target.value })}
                  className="w-full bg-arre-black border border-arre-border px-3 py-2 text-white text-sm focus:outline-none focus:border-arre-red"
                >
                  {CATEGORY_OPTIONS.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                </select>
              </Field>
              <Field label="SKU">
                <TextInput value={form.sku} onChange={(e) => setForm({ ...form, sku: e.target.value })} placeholder="AS-RB-001" />
              </Field>
              <Field label="Descripción corta">
                <TextInput value={form.short_desc} onChange={(e) => setForm({ ...form, short_desc: e.target.value })} placeholder="Marmoleado premium" />
              </Field>
              <Field label="Unidad (ej. 'Desde 350 g')">
                <TextInput value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} placeholder="Desde 350 g" />
              </Field>
              <Field label="Precio base ($ MXN)">
                <TextInput
                  required type="number" min="0" step="1"
                  value={form.price}
                  onChange={(e) => setForm({ ...form, price: e.target.value })}
                  data-testid="product-price-input"
                />
              </Field>
              <Field label="Peso (g)">
                <TextInput type="number" min="0" value={form.weight_g} onChange={(e) => setForm({ ...form, weight_g: e.target.value })} placeholder="1000" />
              </Field>
              <Field label="Stock">
                <TextInput type="number" min="0" value={form.stock} onChange={(e) => setForm({ ...form, stock: e.target.value })} />
              </Field>
              <div className="flex items-center gap-6 pt-6">
                <label className="flex items-center gap-2 text-white/70 text-sm">
                  <Toggle active={form.featured} onChange={(v) => setForm({ ...form, featured: v })} /> Destacado
                </label>
                <label className="flex items-center gap-2 text-white/70 text-sm">
                  <Toggle active={form.active} onChange={(v) => setForm({ ...form, active: v })} /> Activo
                </label>
              </div>
            </div>

            <Field label="Descripción completa">
              <TextArea
                rows={4}
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="El rey de los cortes. Jugoso, marmoleado..."
              />
            </Field>

            <SingleImageUploader
              label="Imagen de portada"
              value={form.image}
              onChange={(url) => setForm({ ...form, image: url })}
            />

            <GalleryUploader
              label="Galería / carrusel (imágenes adicionales del producto)"
              values={form.images}
              onChange={(imgs) => setForm({ ...form, images: imgs })}
            />

            {form.id && form.price !== "" && (
              <div className="text-xs text-white/40 italic">
                Si este producto maneja variantes por peso (350 g / 1 kg), ajusta sus precios individuales
                desde la tabla — este precio base solo aplica cuando no hay variantes.
              </div>
            )}

            {err && <div className="text-arre-red text-sm">{err}</div>}

            <div className="flex justify-end gap-3 pt-2">
              <button type="button" onClick={closeForm} className="px-5 py-3 text-white/60 hover:text-white text-sm tracking-widest uppercase">
                Cancelar
              </button>
              <button
                type="submit"
                disabled={saving}
                className="btn-arre bg-arre-red hover:bg-arre-red-dark text-white font-display text-sm tracking-widest uppercase px-6 py-3 disabled:opacity-50"
                data-testid="save-product-btn"
              >
                {saving ? "Guardando..." : form.id ? "Guardar cambios" : "Crear producto"}
              </button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}

export function Toggle({ active, onChange, testid }) {
  return (
    <button
      type="button"
      onClick={() => onChange(!active)}
      className={`relative w-10 h-5 rounded-full transition ${active ? "bg-arre-red" : "bg-arre-border"}`}
      data-testid={testid}
    >
      <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition ${active ? "left-[22px]" : "left-0.5"}`} />
    </button>
  );
}
